import { useId, useRef, useState, type DragEvent, type ReactNode } from "react";
import { cn } from "../cn";

// The Clevis file field. A "Choose a file" button in the brand, the picked
// file's name and size beside it, drop a file onto it, × to clear. The native
// <input type="file"> is still there (visually hidden) because only it can
// open the device's file picker; nothing of the browser's own is shown.

const fmtSize = (n: number) => (n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

export function FilePicker({
  file,
  onChange,
  accept,
  capture,
  hint = "or drop it here",
  buttonLabel,
  disabled,
  className,
  "aria-label": ariaLabel,
}: {
  file: File | null;
  onChange: (file: File | null) => void;
  /** Same as the native accept list: ".pdf,image/*,application/pdf". Dropped files are checked against it too. */
  accept?: string;
  capture?: "user" | "environment";
  /** Shown while nothing is picked. */
  hint?: ReactNode;
  buttonLabel?: string;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
}) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);

  const accepts = (f: File) => {
    const parts = (accept ?? "").split(",").map((s) => s.trim().toLowerCase()).filter(Boolean);
    if (!parts.length) return true;
    const name = f.name.toLowerCase();
    const type = f.type.toLowerCase();
    return parts.some((p) => (p.startsWith(".") ? name.endsWith(p) : p.endsWith("/*") ? type.startsWith(p.slice(0, -1)) : type === p));
  };
  function onDrop(e: DragEvent) {
    e.preventDefault();
    setOver(false);
    if (disabled) return;
    const f = e.dataTransfer.files?.[0];
    if (f && accepts(f)) onChange(f);
  }

  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border border-dashed border-steel/40 bg-white px-2 py-1.5 text-sm",
        over && "border-brandorange bg-brandorange/5",
        disabled && "opacity-60",
        className,
      )}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={onDrop}
    >
      <input
        ref={inputRef}
        id={id}
        type="file"
        className="peer sr-only"
        accept={accept}
        capture={capture}
        disabled={disabled}
        aria-label={ariaLabel}
        onChange={(e) => {
          onChange(e.target.files?.[0] ?? null);
          e.target.value = ""; // so picking the same file again still fires
        }}
      />
      <label
        htmlFor={id}
        className={cn(
          "inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md bg-ink px-3 py-1 text-xs font-semibold text-white hover:bg-ink-light",
          "peer-focus-visible:ring-2 peer-focus-visible:ring-brandorange peer-focus-visible:ring-offset-2",
          disabled && "pointer-events-none",
        )}
      >
        <svg viewBox="0 0 20 20" className="h-3.5 w-3.5" aria-hidden="true">
          <path d="M10 13V4m0 0L6.5 7.5M10 4l3.5 3.5M4 12.5v2A1.5 1.5 0 0 0 5.5 16h9a1.5 1.5 0 0 0 1.5-1.5v-2" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        {buttonLabel ?? (file ? "Change" : "Choose a file")}
      </label>
      {file ? (
        <>
          <span className="min-w-0 flex-1 truncate text-ink" title={file.name}>
            {file.name} <span className="text-steel">· {fmtSize(file.size)}</span>
          </span>
          <button
            type="button"
            aria-label="Remove file"
            className="shrink-0 rounded-md px-1.5 text-base leading-none text-steel hover:bg-cream hover:text-ink"
            disabled={disabled}
            onClick={() => onChange(null)}
          >
            ×
          </button>
        </>
      ) : (
        <span className="min-w-0 flex-1 truncate text-steel">{hint}</span>
      )}
    </div>
  );
}
